import { useId, useState, useMemo, useRef } from 'react';
import { X } from 'lucide-react';
import { useT } from '../../i18n';
import { PlayerEditModal } from '../players';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface PlayerPickerTeam {
  p1Id: string | null;
  p2Id: string | null;
}

export interface PlayerPickerProps {
  /** All players available to pick from (id, name, elo, …). */
  allPlayers: any[];
  /** Home team (two slots). Controlled by the parent. */
  team1: PlayerPickerTeam;
  /** Away team (two slots). Controlled by the parent. */
  team2: PlayerPickerTeam;
  /** Called with the next home-team object when a slot changes. */
  onChangeTeam1: (next: PlayerPickerTeam) => void;
  /** Called with the next away-team object when a slot changes. */
  onChangeTeam2: (next: PlayerPickerTeam) => void;
  /**
   * Optional: persists a brand-new player created inline from the search field
   * to the global player DB. When omitted, the "create new player" affordance
   * is hidden.
   */
  onPersistNewPlayer?: ((player: any) => Promise<void> | void) | null;
}

/**
 * Four-slot padel team picker (Hjemmelag/Bortelag × Spiller A/B) with an
 * active-slot search panel and inline new-player creation. Extracted verbatim
 * from AddSingleMatchModal so the single-match modal and the booking-form
 * "register match" step share one implementation. team1/team2 are CONTROLLED by
 * the parent (so the parent can derive selection/validation); this component
 * owns only the transient picker UI state (active slot, search, new-player modal).
 */
export function PlayerPicker({
  allPlayers,
  team1,
  team2,
  onChangeTeam1,
  onChangeTeam2,
  onPersistNewPlayer = null,
}: PlayerPickerProps) {
  const [activeSlot, setActiveSlot] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Modal for creating a new player directly from the search field. Uses the
  // same PlayerEditModal as DB administration — so the fields are consistent
  // across the app. When a player is created, it is persisted immediately
  // to the global player DB via onPersistNewPlayer.
  const [showNewPlayerModal, setShowNewPlayerModal] = useState(false);
  const t = useT();
  const titleId = `${safeId('player-picker', useId())}-title`;
  // Focus starts in the search (it used `autoFocus`; the dialog's focus move
  // would override that now).
  const searchRef = useRef<HTMLInputElement | null>(null);

  const playerById = useMemo(() => {
    const map: Record<string, any> = {};
    allPlayers.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [allPlayers]);

  const selectedIds = [team1.p1Id, team1.p2Id, team2.p1Id, team2.p2Id].filter(Boolean);

  // Filter players (hide already selected)
  const filteredPlayers = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = allPlayers.filter((p) => !selectedIds.includes(p.id));
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q));
    return [...list].sort((a, b) => a.name.localeCompare(b.name, 'no'));
  }, [allPlayers, search, selectedIds]);

  // Place player in the active slot, then CLOSE the popup. One popup per slot the
  // user taps — it does not auto-advance to the next slot (the user taps the next
  // slot themselves to open it again).
  const assignToSlot = (playerId: string) => {
    if (!activeSlot) return;

    if (activeSlot === 'L1A') onChangeTeam1({ ...team1, p1Id: playerId });
    else if (activeSlot === 'L1B') onChangeTeam1({ ...team1, p2Id: playerId });
    else if (activeSlot === 'L2A') onChangeTeam2({ ...team2, p1Id: playerId });
    else if (activeSlot === 'L2B') onChangeTeam2({ ...team2, p2Id: playerId });

    setActiveSlot(null);
    setSearch('');
  };

  const clearSlot = (slot: string) => {
    if (slot === 'L1A') onChangeTeam1({ ...team1, p1Id: null });
    else if (slot === 'L1B') onChangeTeam1({ ...team1, p2Id: null });
    else if (slot === 'L2A') onChangeTeam2({ ...team2, p1Id: null });
    else if (slot === 'L2B') onChangeTeam2({ ...team2, p2Id: null });
  };

  // Helper: render one slot (inline, NOT as a component to avoid re-mount issues)
  const renderSlot = (slot: string, label: string, playerId: string | null) => {
    const isActive = activeSlot === slot;
    const player = playerId ? playerById[playerId] : null;
    // THE SLOT OPENS THE PICKER FROM A KEYBOARD TOO (#900). It was a clickable
    // <div> and nothing else, so a keyboard could not choose a player at all,
    // and the picker had no control to give focus back to. The name area is
    // now a <button> inside the same box: preflight leaves a button unstyled,
    // so the slot looks as it did; its click bubbles to the box's handler, so
    // the whole box still opens the picker on a tap. «Fjern» is a real button
    // beside it (it was a `role="button"` span no keyboard could reach). The
    // box's click only repeats the name button, so it is role="presentation".
    return (
      <div
        role="presentation"
        onClick={() => setActiveSlot(slot)}
        className={`w-full px-3 py-2 rounded-lg border text-left transition-all cursor-pointer ${
          isActive
            ? 'border-lime-deep bg-lime-bg/40 ring-2 ring-lime-deep/30'
            : player
              ? 'border-line bg-paper hover:bg-bg-soft'
              : 'border-dashed border-ink-4 bg-bg-soft/40 hover:bg-bg-soft hover:border-ink-2'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <button type="button" className="min-w-0 flex-1 text-left focus-ring rounded">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-0.5">
              {label}
            </div>
            {player ? (
              <div className="text-sm font-medium truncate">{player.name}</div>
            ) : (
              <div className="text-sm text-ink-3 italic">+ Velg spiller</div>
            )}
          </button>
          {player && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearSlot(slot);
              }}
              className="p-1 rounded text-ink-3 hover:text-rose hover:bg-bg-soft cursor-pointer"
              title="Fjern"
              aria-label={t('singleMatches.picker.clearSlot', { player: player.name })}
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Team setup — two columns */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 px-1">
            Hjemmelag
          </div>
          <div className="space-y-1.5">
            {renderSlot('L1A', 'Spiller A', team1.p1Id)}
            {renderSlot('L1B', 'Spiller B', team1.p2Id)}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 px-1">
            Bortelag
          </div>
          <div className="space-y-1.5">
            {renderSlot('L2A', 'Spiller A', team2.p1Id)}
            {renderSlot('L2B', 'Spiller B', team2.p2Id)}
          </div>
        </div>
      </div>

      {/* Player picker — a centered popup shown when a slot is active. A popup
          (rather than an inline panel) keeps the four slots in view and works
          the same in the booking step and the single-match modal. */}
      {activeSlot && (
        // A `Dialog` (#900): named by its «Velg spiller til …» line, focus in the
        // search, Escape closes as the backdrop does, focus back on the slot.
        // It renders INSIDE the single-match modal (itself a Dialog); it mounts
        // later, so it is the innermost one and Escape closes it alone, and its
        // clicks stop at its own edge. The «Opprett ny spiller» modal opens as a
        // SIBLING below, and is innermost in turn while it is up.
        <Dialog
          labelledBy={titleId}
          onClose={() => setActiveSlot(null)}
          showClose={false}
          initialFocus={searchRef}
          backdropClassName="fixed inset-0 flex items-center justify-center p-4"
          backdropStyle={{ zIndex: 80, background: 'rgba(8,10,15,0.55)' }}
          className="w-full max-w-sm rounded-2xl bg-paper border border-line shadow-2xl flex flex-col max-h-[80vh]"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <span
              id={titleId}
              className="font-mono text-[10px] uppercase tracking-wider text-ink-3"
            >
              Velg spiller til{' '}
              {activeSlot === 'L1A' || activeSlot === 'L1B' ? 'hjemmelag' : 'bortelag'}
            </span>
            <button
              type="button"
              onClick={() => setActiveSlot(null)}
              aria-label={t('ui.dialog.close')}
              className="p-1 rounded text-ink-3 hover:text-ink-1 hover:bg-bg-soft transition-colors"
              title="Lukk"
            >
              <X size={16} />
            </button>
          </div>
          <div className="p-3">
            <input
              ref={searchRef}
              type="search"
              placeholder="Søk etter spiller…"
              aria-label={t('players.common.searchLabel')}
              value={search}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={(e) => setSearch(e.target.value)}
              className="input w-full text-sm"
            />
          </div>
          <div className="flex-1 overflow-y-auto border-y border-line bg-bg-soft/30">
            {filteredPlayers.slice(0, 50).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => assignToSlot(p.id)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-lime-bg/30 transition-colors text-left border-b border-line/60 last:border-b-0"
              >
                <span>{p.name}</span>
                <span className="font-mono text-[10px] text-ink-3">ELO {p.elo}</span>
              </button>
            ))}
            {filteredPlayers.length === 0 && (
              <div className="px-3 py-4 text-sm text-ink-3 italic text-center">
                Ingen spillere funnet
              </div>
            )}
          </div>
          {/* Create a new player directly if none match the search */}
          {onPersistNewPlayer && (
            <div className="p-3">
              <button
                type="button"
                onClick={() => setShowNewPlayerModal(true)}
                className="w-full px-3 py-2 rounded-lg border border-dashed border-line-soft text-[12px] text-ink-3 hover:text-ink-1 hover:border-ink-2 hover:bg-bg-soft transition-colors"
              >
                + Opprett ny spiller{search.trim() ? ` «${search.trim()}»` : ''}
              </button>
            </div>
          )}
        </Dialog>
      )}

      {/* Modal for creating a new player. Auto-fills the active slot after
          creation, so the user does not need to click the player in the
          list afterwards. */}
      {showNewPlayerModal && (
        <PlayerEditModal
          mode="create"
          player={search.trim() ? { name: search.trim() } : null}
          onClose={() => setShowNewPlayerModal(false)}
          onSave={async (data: any) => {
            // Build id and full object — same pattern as the setup wizards
            const newId = `c_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
            const newPlayer = {
              id: newId,
              name: data.name,
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email || undefined,
              gender: data.gender,
              elo: data.elo,
              level: Math.min(9, Math.max(1, Math.floor((data.elo - 500) / 300) + 1)),
              isFavorite: false,
              lastPlayed: 0,
              tournamentsPlayed: 0,
              matchesPlayed: 0,
              lastMatchAt: null,
              createdAt: Date.now(),
              isCustom: true,
            };
            // Persist to the global DB (fire-and-forget — UI must not wait)
            if (typeof onPersistNewPlayer === 'function') {
              await onPersistNewPlayer(newPlayer);
            }
            // Auto-select in the active slot
            if (activeSlot) {
              assignToSlot(newId);
            }
            setShowNewPlayerModal(false);
            setSearch('');
          }}
        />
      )}
    </>
  );
}
